import type { Id } from '../../api/types';
import type { BacklogRow } from '../../lib/ticket-tree';

/**
 * Backlog drag-to-reorder, as pure functions over a list of ids.
 *
 * ⭐ Kept out of the component for the reason `features/settings/reorder.ts` gives: "did the drag put
 * it in the right place" has an exact answer, and answering it in a DOM test means simulating pointer
 * events to assert arithmetic.
 *
 * 🔴🔴 REORDERING IS ONLY MEANINGFUL WITHIN ONE SECTION, AND THIS FILE ENFORCES THAT.
 *   `backlog_rank` is a single sparse key per PROJECT, not per sprint — so a rank computed between
 *   two tickets of sprint B is a perfectly valid key that says nothing about sprint A. Dragging a row
 *   from one sprint section to another is a request to CHANGE ITS SPRINT, which is a different write
 *   (`PATCH /tickets/:id {sprintId}`) with different consequences: it moves the ticket's points out of
 *   one sprint's total and into another's, and a sprint may be closed.
 *   ⇒ THE CHOICE MADE HERE: dragging is RESTRICTED TO WITHIN A SECTION. A cross-section drop is
 *     refused and says why, rather than being silently reinterpreted as a sprint change. Doing both
 *     from one gesture would mean a drag that sometimes reorders and sometimes reassigns, with no way
 *     for the user to tell which they are about to get.
 */

export interface RankNeighbours {
  /** The ticket the dragged one should sit AFTER, or null when it lands at the top. */
  afterTicketId: Id | null;
  /** The ticket it should sit BEFORE, or null when it lands at the end. */
  beforeTicketId: Id | null;
}

/**
 * Why a drop cannot be honoured. `null` means it can.
 *
 * ⛔ These are REASONS, not a boolean, because the UI has to say which one — "that did nothing" is
 *   the failure mode this codebase refuses everywhere else.
 */
export type DropRefusal =
  | 'different-section'
  | 'not-reorderable'
  | 'unchanged'
  | 'unknown-row';

/**
 * A row may be dragged only if it is drawn at the top level of its own section.
 *
 * ⛔ A NESTED CHILD IS NOT DRAGGABLE, and this is the rule that keeps `ticket-tree.ts` intact. A
 *   `child` row is drawn under its parent REGARDLESS of its own position — that is the whole contract
 *   — so giving it a rank between two roots would change a number that changes nothing on screen. The
 *   user would drag a row, watch it snap back to its parent, and be right to file a bug.
 * ⭐ A `detached` row IS draggable: it is drawn at the top level, so its position is its own.
 */
export function isReorderable(row: BacklogRow): boolean {
  return row.kind !== 'child';
}

/** The ids a section can reorder, in the order they are drawn. */
export function reorderableIds(rows: readonly BacklogRow[]): Id[] {
  return rows.filter(isReorderable).map((row) => row.ticket.id);
}

/**
 * Where a dragged row ends up, expressed as the neighbours `PATCH /tickets/:id/rank` wants.
 *
 * ⭐ Computed from the RESULTING order rather than from the drop index directly. Deriving "after" and
 *   "before" from an index means getting the off-by-one right twice — once for a downward drag, where
 *   removing the row first shifts every later index, and once for an upward one. Building the array
 *   and reading the neighbours out of it cannot get that wrong.
 */
export function neighboursForDrop(
  order: readonly Id[],
  draggedId: Id,
  targetId: Id,
): RankNeighbours | DropRefusal {
  const from = order.indexOf(draggedId);
  const to = order.indexOf(targetId);

  if (from < 0 || to < 0) return 'unknown-row';
  // ⚠️ Dropping a row on itself is a gesture people make constantly — a click that moved slightly. It
  //    is not an error and it is not a request; it is nothing.
  if (from === to) return 'unchanged';

  const next = [...order];
  const [lifted] = next.splice(from, 1);
  if (lifted === undefined) return 'unknown-row';
  next.splice(to, 0, lifted);

  const at = next.indexOf(draggedId);
  return {
    afterTicketId: at > 0 ? (next[at - 1] as Id) : null,
    beforeTicketId: at < next.length - 1 ? (next[at + 1] as Id) : null,
  };
}

/** The order after a drop — what the list should show while the PATCH is in flight. */
export function orderAfterDrop(order: readonly Id[], draggedId: Id, targetId: Id): Id[] {
  const from = order.indexOf(draggedId);
  const to = order.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return [...order];

  const next = [...order];
  const [lifted] = next.splice(from, 1);
  if (lifted === undefined) return [...order];
  next.splice(to, 0, lifted);
  return next;
}

/**
 * Rearrange a section's raw items into a given top-level order.
 *
 * 🔴 THIS IS WHAT KEEPS SUB-TASK NESTING INTACT THROUGH AN OPTIMISTIC MOVE. `buildBacklogRows` draws
 *   a child under its parent REGARDLESS of the child's own position, so reordering the flat list can
 *   never separate the two — the child is found by `childrenOf(parent)`, not by where it sits here.
 *   ⇒ Reordering the input is therefore safe, and it is the reason this is a reshuffle of `items`
 *     rather than a reshuffle of the built rows, which WOULD break the nesting.
 *
 * ⚠️ Items not named in `order` keep their identity and go last. Those are the children whose parent
 *   is on this page — `buildBacklogRows` skips them at their own position anyway, so where they sit
 *   in the input cannot change what is drawn. ⛔ They are NOT dropped: an id missing from `order`
 *   must still reach the tree builder, or a row disappears.
 */
export function applyOrder<T extends { id: Id }>(items: readonly T[], order: readonly Id[]): T[] {
  const rank = new Map(order.map((id, index) => [id, index]));
  const ranked = items
    .filter((item) => rank.has(item.id))
    .sort((a, b) => (rank.get(a.id) as number) - (rank.get(b.id) as number));
  const rest = items.filter((item) => !rank.has(item.id));
  return [...ranked, ...rest];
}

/**
 * The message shown when a drop is refused.
 *
 * ⚠️ EACH ONE NAMES THE ALTERNATIVE. "You cannot do that" leaves the user with a gesture and no next
 *   step; the cross-section case in particular has a real answer — open the ticket and change its
 *   sprint — and saying so is the difference between a limitation and a dead end.
 */
export const REFUSAL_MESSAGE: Record<Exclude<DropRefusal, 'unchanged'>, string> = {
  'different-section':
    'A ticket can only be reordered within its own section. Dragging it to another sprint would be a change of sprint, not of order — open the ticket and change its sprint instead.',
  'not-reorderable':
    'A sub-task is drawn under its parent whatever its own rank, so reordering it here would change a number and nothing else. Reorder its parent instead.',
  'unknown-row':
    'That row is no longer in this section — the list has changed since the drag started. Refresh and try again.',
};
